import { IconChevronDown, IconSearch } from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSessionPanel } from "#app/components/lobe-chat/session-panel/use-session-panel";
import { ConfirmDialog } from "#app/components/new-ui/confirm-dialog";
import { Button } from "#app/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "#app/components/ui/dialog";
import { RailTooltip } from "#app/components/ui/tooltip";
import { cn } from "#app/utils/misc.tsx";
import { ChatListItem } from "./chat-list-item";

type RecentChatsProps = {
  /** Routes through the sidebar's design-phase navigation guard. */
  onNavigate: (to: string) => void;
  collapsed: boolean;
};

/**
 * "Recent" chat list in the sidebar. Reuses `useSessionPanel`, the same hook the
 * in-chat session panel uses, so the list, search filter and delete flow stay in
 * sync with the chat screen.
 */
export function RecentChats({ onNavigate, collapsed }: RecentChatsProps) {
  const { t } = useTranslation(["nav", "chat", "common"]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const {
    currentSessionId,
    filteredChatDataSource,
    isLoadingChats,
    chatsError,
    hasMoreChats,
    loadMoreChats,
    searchQuery,
    setSearchQuery,
    deletingChatId,
    deleteModalOpen,
    deleteTarget,
    handleDeleteChat,
    confirmDelete,
    cancelDelete,
  } = useSessionPanel();

  const searchLabel = t("nav:searchConversationsTitle");

  const openSearchDialog = useCallback(() => {
    setSearchQuery("");
    setSearchOpen(true);
  }, [setSearchQuery]);

  const handleSearchOpenChange = useCallback(
    (open: boolean) => {
      setSearchOpen(open);
      if (!open) setSearchQuery("");
    },
    [setSearchQuery],
  );

  const selectChat = (id: string) => {
    setSearchOpen(false);
    setSearchQuery("");
    onNavigate(`/u/alpha/agent/${id}`);
  };

  /**
   * Four states, not two. This used to render `noChats` for anything that
   * produced an empty array — so a request that never came back, or came back
   * 401/500, was indistinguishable from genuinely having no conversations, and
   * there was nothing the user could do about it but reload the page. The hook
   * has always reported `isLoadingChats` / `chatsError`; the list simply threw
   * them away.
   */
  const isSearching = searchQuery.trim().length > 0;

  const renderList = (items: typeof filteredChatDataSource) => {
    if (items.length > 0) {
      return items.map((chat) => (
        <ChatListItem
          key={chat.key}
          title={chat.title}
          isActive={chat.key === currentSessionId}
          isDeleting={chat.key === deletingChatId}
          onSelect={() => selectChat(chat.key)}
          onDelete={() => handleDeleteChat(chat.key, chat.title)}
          deleteLabel={t("common:delete")}
          moreLabel={t("common:ui.moreActions")}
        />
      ));
    }

    // Pagination while a search filter is empty must not replace the empty
    // state with "Loading…" — the tall search dialog keeps the end sentinel
    // in view, so load-more fires and flickered "Loading more..." in the gap.
    if (isLoadingChats && !isSearching) {
      return (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          {t("common:loading")}
        </p>
      );
    }

    if (chatsError && !isSearching) {
      return (
        <div className="space-y-2 px-3 py-2">
          <p className="text-sm text-muted-foreground">
            {t("nav:chatsLoadFailed")}
          </p>
          <Button
            variant="secondary"
            size="xs"
            onClick={() => void loadMoreChats(true)}
          >
            {t("nav:retryLoadChats")}
          </Button>
        </div>
      );
    }

    return (
      <p className="px-3 py-2 text-sm text-muted-foreground">
        {isSearching ? t("common:ui.noResults") : t("nav:noChats")}
      </p>
    );
  };

  const deleteDialog = (
    <ConfirmDialog
      open={deleteModalOpen}
      onOpenChange={(open) => {
        if (!open) cancelDelete();
      }}
      destructive
      title={t("common:delete")}
      description={t("chat:sessionPanel.confirmDelete", {
        name: deleteTarget?.name ?? "",
      })}
      confirmText={t("common:delete")}
      cancelText={t("common:cancel")}
      onConfirm={() => void confirmDelete()}
      onCancel={cancelDelete}
    />
  );

  const searchDialog = (
    <Dialog open={searchOpen} onOpenChange={handleSearchOpenChange}>
      <DialogContent
        showCloseButton={true}
        className={cn("sm:max-w-2xl gap-0 overflow-hidden p-0")}
      >
        <div className="border-b px-5 py-3">
          {/* Hidden, not removed: `DialogTitle` is what gives the dialog its
              accessible name, so commenting it out left a screen reader
              announcing an unnamed dialog. `sr-only` keeps the heading off the
              screen -- which is the visual change intended here -- while the
              name survives. Same pattern as settings-modal.tsx. */}
          <DialogTitle className="sr-only">{searchLabel}</DialogTitle>
          <div className="flex h-10 items-center gap-3">
            <IconSearch className="h-5 w-5 text-zinc-500" />
            <input
              autoFocus
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("nav:searchChats")}
              aria-label={searchLabel}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400"
            />
          </div>
        </div>

        <ChatScrollList
          className="h-115 px-3 py-4 pr-5"
          // Client-side title filter: an empty result set still leaves the end
          // sentinel on screen in this fixed-height dialog, which kept paging
          // and flashing "Loading more...". Paginate only the unfiltered list.
          hasMore={hasMoreChats && !isSearching}
          isLoading={isLoadingChats && !isSearching}
          itemCount={filteredChatDataSource.length}
          loadMore={loadMoreChats}
          loadingLabel={t("chat:loadingMore", { defaultValue: "Loading…" })}
        >
          <div className="space-y-1">{renderList(filteredChatDataSource)}</div>
        </ChatScrollList>
      </DialogContent>
    </Dialog>
  );

  if (collapsed) {
    return (
      <div className="flex justify-center">
        <RailTooltip title={searchLabel}>
          <Button
            variant="ghost"
            size="icon"
            onClick={openSearchDialog}
            aria-label={searchLabel}
            className="-mx-2"
          >
            <IconSearch className="h-5 w-5" />
          </Button>
        </RailTooltip>
        {searchDialog}
        {deleteDialog}
      </div>
    );
  }

  return (
    <div className="recent-block mt-5 -mx-2 -mr-3">
      <div className="flex items-center justify-between pr-5">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="ml-3 flex cursor-pointer items-center gap-1 text-[13px] text-zinc-500/80 hover:text-black dark:text-zinc-400 dark:hover:text-white"
        >
          {t("nav:recentConversations")}
          <motion.span
            animate={{ rotate: expanded ? 0 : -90 }}
            transition={{ duration: 0.2 }}
            className="inline-flex"
          >
            <IconChevronDown size={18} />
          </motion.span>
        </button>

        <Button
          variant="secondary"
          size="xs"
          onClick={openSearchDialog}
          aria-label={searchLabel}
        >
          <IconSearch />
          {t("common:search")}
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <ChatScrollList
              className="mt-3 h-[calc(100dvh-340px)] pr-3"
              // Same as the dialog: a short filtered list keeps the end
              // sentinel on screen, so paging refires and "Loading…" blinks
              // for as long as the query is typed.
              hasMore={hasMoreChats && !isSearching}
              isLoading={isLoadingChats && !isSearching}
              itemCount={filteredChatDataSource.length}
              loadMore={loadMoreChats}
              loadingLabel={t("chat:loadingMore", {
                defaultValue: "Loading…",
              })}
            >
              <div className="space-y-1">
                {renderList(filteredChatDataSource)}
              </div>
            </ChatScrollList>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {searchDialog}
      {deleteDialog}
    </div>
  );
}

type ChatScrollListProps = {
  children: React.ReactNode;
  className?: string;
  hasMore: boolean;
  isLoading: boolean;
  itemCount: number;
  loadMore: (reset?: boolean) => void | Promise<void>;
  loadingLabel: string;
};

/**
 * Same infinite-scroll contract as the in-chat session panel: near-bottom scroll
 * + an end sentinel IntersectionObserver. The sidebar used to render only the
 * first page because it never called `loadMore`.
 */
function ChatScrollList({
  children,
  className,
  hasMore,
  isLoading,
  itemCount,
  loadMore,
  loadingLabel,
}: ChatScrollListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const tryLoadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      void loadMore();
    }
  }, [hasMore, isLoading, loadMore]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 120) {
      tryLoadMore();
    }
  }, [tryLoadMore]);

  useLayoutEffect(() => {
    const sentinel = endRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          tryLoadMore();
        }
      },
      { root, rootMargin: "0px 0px 80px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, itemCount, tryLoadMore]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={cn("overflow-y-auto overscroll-contain", className)}
    >
      {children}
      {hasMore ? (
        <div
          ref={endRef}
          className="py-2 text-center text-xs text-muted-foreground"
        >
          {/* Only when rows exist — an empty list + visible sentinel used to
              flash this label while search had no matches. */}
          {isLoading && itemCount > 0 ? loadingLabel : null}
        </div>
      ) : null}
    </div>
  );
}

export default RecentChats;
